import React, { useRef, useEffect, forwardRef, useCallback, type ForwardedRef } from "react";
import clsx from "clsx";

type AutoResizeTextareaProps = {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    disabled?: boolean;
    placeholder?: string;
    maxLines?: number;
    lineHeight?: number;
    className?: string;
    autoFocus?: boolean;
};

/**
 * A reusable auto-resizing textarea component that adjusts its height based on content.
 * The textarea will grow up to a maximum number of lines before becoming scrollable.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.value - The controlled input value
 * @param {Function} props.onChange - Handler for value changes
 * @param {Function} [props.onKeyDown] - Optional handler for key press events
 * @param {boolean} [props.disabled=false] - Whether the textarea is disabled
 * @param {string} [props.placeholder="Type your message here.."] - Placeholder text
 * @param {number} [props.maxLines=3] - Maximum number of lines before scrolling
 * @param {number} [props.lineHeight=24] - Line height in pixels
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.autoFocus=false] - Whether the textarea should be auto-focused
 * @returns {JSX.Element} A styled auto-resizing textarea element
 */
const AutoResizeTextarea = forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
    (
        {
            value,
            onChange,
            onKeyDown,
            disabled,
            placeholder = "Type your message here..",
            maxLines = 3,
            lineHeight = 24,
            className = "",
            autoFocus = false,
        },
        ref: ForwardedRef<HTMLTextAreaElement>,
    ) => {
        const internalRef = useRef<HTMLTextAreaElement>(null);
        const textareaRef = ref || internalRef;

        /**
         * Adjusts the textarea height based on its content.
         * The textarea will grow up to maxLines before becoming scrollable.
         *
         * @callback
         * @returns {void}
         */
        const adjustTextareaHeight = useCallback(() => {
            const textarea = textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>;
            if (textarea?.current) {
                textarea.current.style.height = "auto";
                const maxHeight = lineHeight * maxLines;

                if (textarea.current.scrollHeight > maxHeight) {
                    textarea.current.style.height = `${maxHeight}px`;
                    textarea.current.style.overflowY = "auto";
                } else {
                    textarea.current.style.height = `${textarea.current.scrollHeight}px`;
                    textarea.current.style.overflowY = "hidden";
                }
            }
        }, [lineHeight, maxLines, textareaRef]);

        useEffect(() => {
            adjustTextareaHeight();
        }, [value, adjustTextareaHeight]);

        return (
            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                disabled={disabled}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className={clsx(
                    "tw-flex-1 tw-px-4 tw-py-2 tw-rounded-[8px] tw-border tw-border-gray-300",
                    "focus:tw-outline-none focus:tw-border-purple-500",
                    "tw-resize-none tw-overflow-y-auto",
                    "tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent",
                    className,
                )}
                rows={1}
            />
        );
    },
);

AutoResizeTextarea.displayName = "AutoResizeTextarea";

export default AutoResizeTextarea;
